// Copied from the app because this entry must not import app modules — a shared module would
// land in a chunk the loader waits for. sync-guards.test.ts holds every copy to its source and
// fails the build on a drift; add a value here, add its guard there.
const LIGHT_ONLY_ROUTES = ['/entity-investor', '/business-coowner', '/square'];

// Written through useLocalStorage, hence JSON-encoded.
const TAILWIND_MODE_KEY = 'color-theme';

// Each background is what the app paints on mount, and the colour is what its Loader spinner
// paints, so neither jumps at the handover. The light colour is spinner.css's default.
const DARK = { background: 'hsl(0 0% 8.6%)', color: 'hsl(153.1 71.2% 44.9%)' };
const LIGHT = { background: 'hsl(45 20% 96.1%)' };

// Mirrors DARK_THEME_CLASSNAME in src/constants.ts.
export const TAILWIND_DARK_CLASS = 'dark';
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

export type ThemeEnvironment = {
    pathname: string;
    readStorage: (key: string) => string | null;
    prefersDark: boolean;
};

export type BootTheme = {
    /** Background to paint while the bundle loads, or undefined to leave the page as it is. */
    background?: string;
    /** Spinner and bar colour; the bar's groove is mixed from it. */
    color?: string;
    /**
     * Whether <body> needs Tailwind's dark class up front. The admin stylesheet arrives with the
     * lazy admin chunk and paints <body> from the light `:root` variables until the class is
     * there, and useColorMode only sets it after mount — the gap is a light flash.
     */
    tailwindDark: boolean;
};

/** What sync-guards.test.ts checks against the app. */
export const APP_CONTRACT = {
    lightOnlyRoutes: LIGHT_ONLY_ROUTES,
    tailwindModeKey: TAILWIND_MODE_KEY,
    dark: DARK,
    light: LIGHT,
    tailwindDarkClass: TAILWIND_DARK_CLASS,
    darkSchemeQuery: DARK_SCHEME_QUERY,
} as const;

export function resolveBootTheme({
    pathname,
    readStorage,
    prefersDark,
}: ThemeEnvironment): BootTheme {
    // These routes mount a hard-coded light theme, whatever the stored mode says.
    if (LIGHT_ONLY_ROUTES.some((route) => isUnder(pathname, route))) {
        return { ...LIGHT, tailwindDark: false };
    }

    const dark = isTailwindDark(readStorage, prefersDark);
    return { ...(dark ? DARK : LIGHT), tailwindDark: dark };
}

function isUnder(pathname: string, root: string): boolean {
    return pathname === root || pathname.startsWith(`${root}/`);
}

function isTailwindDark(
    readStorage: ThemeEnvironment['readStorage'],
    prefersDark: boolean
): boolean {
    const stored = readStorage(TAILWIND_MODE_KEY);
    if (stored === '"dark"' || stored === 'dark') return true;
    if (stored === '"light"' || stored === 'light') return false;

    return prefersDark;
}
